import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './VisitResturants.css';
import CouponBanner from './CouponBanner';

const configuredApiBase =
  typeof process !== 'undefined' && process.env
    ? process.env.REACT_APP_GOOURHUB_BUSINESS_API
    : '';

const API_BASE_URL = (
  configuredApiBase || 'https://business-api.goourhub.com'
).replace(/\/+$/, '');

const RESTAURANT_CATEGORY = 'restaurants';
const PAGE_LIMIT = 200;
const MAX_TILT = 8;

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
};

const parseDetails = (business) => {
  if (
    business?.details &&
    typeof business.details === 'object' &&
    !Array.isArray(business.details)
  ) {
    return business.details;
  }

  if (
    business?.details_json &&
    typeof business.details_json === 'object' &&
    !Array.isArray(business.details_json)
  ) {
    return business.details_json;
  }

  if (typeof business?.details_json === 'string') {
    try {
      const parsed = JSON.parse(business.details_json);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      console.warn('Unable to parse business details_json.', error);
    }
  }

  return {};
};

const buildLocationAddress = (details) => {
  if (!Array.isArray(details?.locations) || details.locations.length === 0) {
    return '';
  }

  const location = details.locations[0];

  if (typeof location === 'string') {
    return location.trim();
  }

  if (!location || typeof location !== 'object') {
    return '';
  }

  const directAddress = firstText(
    location.full_address,
    location.fullAddress,
    location.address
  );

  if (directAddress) {
    return directAddress;
  }

  return [
    firstText(location.street_address, location.streetAddress, location.street),
    location.city,
    location.state,
    firstText(location.zip_code, location.zipCode, location.zip),
  ]
    .filter(Boolean)
    .join(', ');
};

const normalizeBusiness = (business) => {
  const details = parseDetails(business);
  const profilePath = firstText(
    business?.profile_path,
    business?.profilePath,
    details.profile_path,
    details.profilePath
  );
  const website = firstText(
    business?.website,
    business?.url,
    details.website,
    details.url
  );
  const source = firstText(business?.source, details.source).toLowerCase();
  const listingPlan = firstText(
    business?.listing_plan,
    business?.listingPlan,
    details.listing_plan,
    details.listingPlan
  ).toLowerCase();
  const paymentStatus = firstText(
    business?.payment_status,
    business?.paymentStatus,
    details.payment_status,
    details.paymentStatus
  ).toLowerCase();

  const isPremium =
    ['premium', 'featured', 'paid'].includes(listingPlan) ||
    ['paid', 'complete', 'completed', 'succeeded'].includes(paymentStatus) ||
    Boolean(details.legacy_premium_label);

  const isOwnerSubmitted =
    Boolean(business?.email_verified_at || business?.emailVerifiedAt) ||
    source.includes('partner') ||
    source.includes('add business') ||
    source.includes('owner');

  return {
    ...business,
    id: firstText(business?.id, details.canonical_id) || business?.name,
    name: firstText(business?.name, details.name) || 'Business',
    icon: firstText(business?.icon, details.icon) || '🍽️',
    address: firstText(
      business?.address,
      business?.full_address,
      business?.fullAddress,
      details.address,
      details.full_address,
      details.fullAddress,
      buildLocationAddress(details)
    ),
    phone: firstText(
      business?.phone_number,
      business?.phoneNumber,
      business?.phone,
      details.phone_number,
      details.phoneNumber,
      details.phone
    ),
    website,
    mapUrl: firstText(
      business?.map_url,
      business?.mapUrl,
      details.map_url,
      details.mapUrl
    ),
    profilePath,
    isPremium,
    isOwnerSubmitted,
  };
};

const buildMapUrl = (item) =>
  item.mapUrl ||
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    item.address || item.name || ''
  )}`;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const fetchRestaurantPage = async (page, signal) => {
  const requestUrl =
    `${API_BASE_URL}/api/businesses` +
    `?category=${encodeURIComponent(RESTAURANT_CATEGORY)}` +
    `&page=${page}` +
    `&limit=${PAGE_LIMIT}` +
    '&sort=name';

  const response = await fetch(requestUrl, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
    signal,
  });

  let payload;

  try {
    payload = await response.json();
  } catch {
    throw new Error('The restaurant service returned an invalid response.');
  }

  if (!response.ok || payload?.success === false) {
    throw new Error(
      payload?.message || `Unable to load restaurants (${response.status}).`
    );
  }

  if (!Array.isArray(payload?.businesses)) {
    throw new Error('The restaurant service did not return a business list.');
  }

  return payload;
};

function NeuTile({ item, onClick }) {
  const ref = useRef(null);
  const unverified = !item.isOwnerSubmitted;

  const handleMove = (event) => {
    const element = ref.current;

    if (!element || prefersReducedMotion()) {
      return;
    }

    const rectangle = element.getBoundingClientRect();
    const pointerX = (event.clientX - rectangle.left) / rectangle.width;
    const pointerY = (event.clientY - rectangle.top) / rectangle.height;

    element.style.transform =
      `rotateX(${(0.5 - pointerY) * MAX_TILT}deg) ` +
      `rotateY(${(pointerX - 0.5) * MAX_TILT}deg)`;

    element.style.setProperty('--gx', `${pointerX * 100}%`);
    element.style.setProperty('--gy', `${pointerY * 100}%`);
  };

  const handleLeave = () => {
    const element = ref.current;

    if (element) {
      element.style.transform = 'rotateX(0deg) rotateY(0deg)';
    }
  };

  return (
    <div
      ref={ref}
      className="new-tile tile-clickable"
      role="button"
      tabIndex={0}
      onClick={() => onClick(item)}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick(item);
        }
      }}
    >
      <span className="tile-glare" aria-hidden="true" />

      {item.isPremium && <span className="top-ribbon">Premium</span>}

      <span className="icon-for-new-tile">{item.icon}</span>

      <span className="tile-name">
        {item.name}

        {unverified && (
          <span className="info-badge" title="Unverified listing">
            ⓘ
          </span>
        )}
      </span>
    </div>
  );
}

const CategorySection = ({ title, items, onTileClick }) => (
  <div className="category-section">
    {title && <h2 className="category-header">{title}</h2>}

    <div className="new-tile-container-new">
      {items.map((item, index) => (
        <NeuTile
          key={item.id || `${item.name}-${index}`}
          item={item}
          onClick={onTileClick}
        />
      ))}
    </div>
  </div>
);

function VisitResturants() {
  const [restaurants, setRestaurants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [reloadNumber, setReloadNumber] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();

    const loadRestaurants = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        const firstPage = await fetchRestaurantPage(1, controller.signal);
        const totalPages = Math.max(
          1,
          Number(firstPage?.pagination?.totalPages) || 1
        );

        const allBusinesses = [...firstPage.businesses];

        for (let page = 2; page <= totalPages; page += 1) {
          const pagePayload = await fetchRestaurantPage(page, controller.signal);
          allBusinesses.push(...pagePayload.businesses);
        }

        const uniqueBusinesses = new Map();

        allBusinesses.forEach((business, index) => {
          const fallbackKey = [
            business?.name,
            business?.address,
            business?.phone_number,
            index,
          ].join('|');

          uniqueBusinesses.set(business?.id || fallbackKey, business);
        });

        const normalizedRestaurants = Array.from(uniqueBusinesses.values())
          .map(normalizeBusiness)
          .sort((left, right) =>
            left.name.localeCompare(right.name, undefined, {
              sensitivity: 'base',
            })
          );

        if (!controller.signal.aborted) {
          setRestaurants(normalizedRestaurants);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && !controller.signal.aborted) {
          console.error('Unable to load restaurant businesses.', error);
          setErrorMessage(
            error.message || 'Unable to load restaurants. Please try again.'
          );
          setRestaurants([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadRestaurants();

    return () => controller.abort();
  }, [reloadNumber]);

  const handleTileClick = (item) => {
    if (item.profilePath?.startsWith('/')) {
      navigate(item.profilePath);
      return;
    }

    setSelected(item);
  };

  const closePopup = () => setSelected(null);

  return (
    <div className="categories-container">
      <CouponBanner />

      {loading && (
        <div className="category-section">
          <h2 className="category-header">Local Restaurants</h2>
          <p role="status">Loading restaurants...</p>
        </div>
      )}

      {!loading && errorMessage && (
        <div className="category-section">
          <h2 className="category-header">Local Restaurants</h2>
          <p role="alert">{errorMessage}</p>
          <button type="button" onClick={() => setReloadNumber((value) => value + 1)}>
            Try Again
          </button>
        </div>
      )}

      {!loading && !errorMessage && restaurants.length === 0 && (
        <div className="category-section">
          <h2 className="category-header">Local Restaurants</h2>
          <p>No approved restaurants are currently available.</p>
        </div>
      )}

      {!loading && !errorMessage && restaurants.length > 0 && (
        <CategorySection
          title={`Local Restaurants (${restaurants.length})`}
          items={restaurants}
          onTileClick={handleTileClick}
        />
      )}

      <div className="back-home-tile">
        <Link to="/" className="back-home-link">
          Go Back to Homepage
        </Link>
      </div>

      {selected && (
        <div className="popup-overlay" onClick={closePopup}>
          <div
            className="popup-box"
            role="dialog"
            aria-modal="true"
            aria-labelledby="restaurant-popup-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="restaurant-popup-title">{selected.name}</h3>

            {selected.address && (
              <p>
                <strong>Address:</strong> {selected.address}
              </p>
            )}

            {selected.phone && (
              <p>
                <strong>Phone:</strong> {selected.phone}
              </p>
            )}

            {(selected.address || selected.mapUrl) && (
              <p>
                <a
                  href={buildMapUrl(selected)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="map-link-button"
                >
                  📍 Take Me There
                </a>
              </p>
            )}

            {selected.website && (
              <p>
                <a
                  href={selected.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="business-website-link"
                >
                  {selected.website
                    .replace(/^https?:\/\//, '')
                    .replace(/\/$/, '')}
                </a>
              </p>
            )}

            <button type="button" onClick={closePopup}>
              Close
            </button>

            {!selected.isOwnerSubmitted && (
              <>
                <hr className="popup-divider" />
                <p className="disclaimer-text">
                  Disclaimer: This listing has not been claimed or verified by
                  the business owner. Information provided is based on publicly
                  available sources.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default VisitResturants;
